import type {
  CompanyDetail,
  OfflinePackDto,
  OfflinePackResponse,
  ProductSummary,
  RepresentativeSummary,
} from "@mvk/shared";
import { apiConfig, isApiEnabled } from "../api/config";
import { visitorAuthStorage } from "../api/authStorage";
import { idbGetPack, idbPutPack } from "./offlineDb";
import { appConfig } from "../config";
import { debugLog } from "./debugLog";
import i18n, { normalizeLocale } from "../i18n";

type PackRecord = {
  eventId: string;
  lang: string;
  packVersion: string;
  syncedAt: string;
  pack: OfflinePackDto;
};

const memory = new Map<string, PackRecord>();

function offlineEnabled() {
  return appConfig.featureFlags.enableOfflineMode;
}

function packKey(eventId: string, lang: string) {
  return `${eventId}:${lang}`;
}

export const eventPackService = {
  clearCache(): void {
    memory.clear();
  },

  async getCached(eventId: string): Promise<OfflinePackDto | null> {
    if (!offlineEnabled()) return null;
    const lang = normalizeLocale(i18n.language);
    const key = packKey(eventId, lang);
    const mem = memory.get(key);
    if (mem) return mem.pack;
    const stored = await idbGetPack<PackRecord>(eventId);
    if (stored?.pack && (stored.lang ?? "ru") === lang) {
      memory.set(key, stored);
      return stored.pack;
    }
    return null;
  },

  async getCompany(eventId: string, companyId: string): Promise<CompanyDetail | null> {
    const pack = await this.getCached(eventId);
    return pack?.companies.find((c) => c.id === companyId) ?? null;
  },

  async listCompanies(eventId: string): Promise<CompanyDetail[]> {
    const pack = await this.getCached(eventId);
    return pack?.companies ?? [];
  },

  async listProducts(eventId: string, companyId?: string): Promise<ProductSummary[]> {
    const pack = await this.getCached(eventId);
    const products = pack?.products ?? [];
    return companyId ? products.filter((p) => p.companyId === companyId) : products;
  },

  async listRepresentatives(eventId: string, companyId?: string): Promise<RepresentativeSummary[]> {
    const pack = await this.getCached(eventId);
    const reps = pack?.representatives ?? [];
    return companyId ? reps.filter((r) => r.companyId === companyId) : reps;
  },

  async resolveCode(eventId: string, code: string) {
    const pack = await this.getCached(eventId);
    if (!pack) return null;
    const trimmed = code.trim();
    return pack.codes[trimmed] ?? pack.codes[trimmed.toLowerCase()] ?? null;
  },

  /** Fetch pack from API and persist. Returns pack (or cached on 304 / network fail). */
  async sync(eventId: string): Promise<OfflinePackDto | null> {
    if (!offlineEnabled() || !isApiEnabled() || !visitorAuthStorage.isAuthenticated()) {
      return this.getCached(eventId);
    }

    const lang = normalizeLocale(i18n.language);
    const cached = await this.getCached(eventId);
    const headers: Record<string, string> = {
      Accept: "application/json",
      Authorization: `Bearer ${visitorAuthStorage.getToken()}`,
      "Accept-Language": lang,
    };
    if (cached?.packVersion) {
      headers["If-None-Match"] = `"${cached.packVersion}"`;
    }

    try {
      const res = await fetch(
        `${apiConfig.baseUrl}/events/${eventId}/offline-pack?lang=${lang}`,
        { headers },
      );
      if (res.status === 304 && cached) {
        debugLog.info("offline", "pack not modified", { eventId, version: cached.packVersion, lang });
        return cached;
      }
      if (!res.ok) {
        debugLog.warn("offline", `pack fetch ${res.status}`, { eventId, lang });
        return cached;
      }
      const data = (await res.json()) as OfflinePackResponse;
      const record: PackRecord = {
        eventId,
        lang,
        packVersion: data.pack.packVersion,
        syncedAt: new Date().toISOString(),
        pack: data.pack,
      };
      memory.set(packKey(eventId, lang), record);
      await idbPutPack(record);
      debugLog.info("offline", "pack synced", {
        eventId,
        lang,
        version: data.pack.packVersion,
        companies: data.pack.companies.length,
        products: data.pack.products.length,
      });
      return data.pack;
    } catch (err) {
      debugLog.warn("offline", "pack sync failed, using cache", err);
      return cached;
    }
  },
};
